import { CampaignTableRow } from "./campaignTableTypes";

export type BulkAction = "activate" | "deactivate" | "archive" | "unarchive";

export const BULK_ACTIONS: BulkAction[] = ["activate", "deactivate", "archive", "unarchive"];

export const BULK_ACTION_LABELS: Record<BulkAction, string> = {
  activate: "Activate",
  deactivate: "Deactivate",
  archive: "Archive",
  unarchive: "Unarchive",
};

/**
 * A row qualifies for an action only in the states the rest of the app allows:
 * archiving stays blocked while a campaign is active, and campaigns that are
 * still being created can never be touched.
 */
const isRowEligible = (row: CampaignTableRow, action: BulkAction): boolean => {
  if (row.processing) return false;

  switch (action) {
    case "activate":
      return !row.active && !row.archived;
    case "deactivate":
      return row.active;
    case "archive":
      return !row.active && !row.archived;
    case "unarchive":
      return row.archived;
    default:
      return false;
  }
};

export const isActionEnabled = (rows: CampaignTableRow[], action: BulkAction): boolean =>
  rows.length > 0 && rows.every((row) => isRowEligible(row, action));

const DISABLED_REASONS: Record<BulkAction, string> = {
  activate: "Only inactive, non-archived campaigns can be activated.",
  deactivate: "Only active campaigns can be deactivated.",
  archive: "Deactivate a campaign before archiving it. Archived campaigns cannot be archived again.",
  unarchive: "Only archived campaigns can be unarchived.",
};

export const getDisabledReason = (rows: CampaignTableRow[], action: BulkAction): string => {
  if (isActionEnabled(rows, action)) return "";
  if (rows.length === 0) return "Select at least one campaign.";
  if (rows.some((row) => row.processing)) return "Some selected campaigns are still being created.";
  return DISABLED_REASONS[action];
};

export const CONFIRM_COPY: Record<
  BulkAction,
  { title: string; body: (count: number) => string; confirmLabel: string }
> = {
  activate: {
    title: "Activate campaigns",
    body: (count) =>
      `${count} campaign${count === 1 ? "" : "s"} will start delivering. Their line items will be activated too.`,
    confirmLabel: "Activate",
  },
  deactivate: {
    title: "Deactivate campaigns",
    body: (count) =>
      `${count} campaign${count === 1 ? "" : "s"} will stop delivering. Their line items will be paused too.`,
    confirmLabel: "Deactivate",
  },
  archive: {
    title: "Archive campaigns",
    body: (count) =>
      `${count} campaign${
        count === 1 ? "" : "s"
      } will be removed from the campaign selector options. You can unarchive them later from this dashboard.`,
    confirmLabel: "Archive",
  },
  unarchive: {
    title: "Unarchive campaigns",
    body: (count) =>
      `${count} campaign${
        count === 1 ? "" : "s"
      } will be restored to the campaign selector options. They stay inactive until you activate them.`,
    confirmLabel: "Unarchive",
  },
};

export const PAST_TENSE: Record<BulkAction, string> = {
  activate: "activated",
  deactivate: "deactivated",
  archive: "archived",
  unarchive: "unarchived",
};
